import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";
import { plansAdminApi } from "../lib/plansApi";
import PlanCard, { FEATURE_LABELS } from "../components/PlanCard";

const brl = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL", minimumFractionDigits: 2, maximumFractionDigits: 2 });

const DEFAULT_FEATURES = Object.keys(FEATURE_LABELS).reduce((acc, k) => ({ ...acc, [k]: false }), {});
const DEFAULT_LIMITS = { clients: -1, professionals: -1, storage_gb: -1 };

const AdminPlans = () => {
    const [plans, setPlans] = useState([]);
    const [distribution, setDistribution] = useState(null);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState("hub");       // hub | plans | distribution
    const [editing, setEditing] = useState(null);  // null | plan object | "new"
    const [previewing, setPreviewing] = useState(null); // plan object for preview overlay

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [list, dist] = await Promise.all([
                plansAdminApi.list(),
                plansAdminApi.distribution(),
            ]);
            setPlans(list);
            setDistribution(dist);
        } catch (e) {
            toast.error("Falha ao carregar planos", { description: e.message });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const kpis = useMemo(() => {
        if (!distribution) return { active: 0, popular: "—", mrr: 0 };
        return {
            active: distribution.total_plans_active || 0,
            popular: distribution.most_popular?.name || "—",
            mrr: distribution.total_mrr || 0,
        };
    }, [distribution]);

    // ─── HUB ───────────────────────────────────────────────────────────
    if (view === "hub") {
        return (
            <div data-testid="admin-plans" className="space-y-6">
                <header className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500">Painel Mestre</div>
                        <h2 className="salon-heading text-2xl text-white">Gestão de Planos</h2>
                        <p className="text-xs text-slate-500 mt-1 max-w-lg">
                            Cria, edita e desativa planos comerciais. Alterações não afetam tenants já assinantes — apenas novos cadastros usam a nova configuração.
                        </p>
                    </div>
                </header>

                {/* KPIs */}
                <div className="grid sm:grid-cols-3 gap-3">
                    <KpiCard testId="plans-kpi-active" icon="fa-toggle-on" label="Planos ativos" value={kpis.active} />
                    <KpiCard testId="plans-kpi-popular" icon="fa-star" label="Mais popular" value={kpis.popular} />
                    <KpiCard testId="plans-kpi-mrr" icon="fa-coins" label="MRR total" value={brl(kpis.mrr)} />
                </div>

                {/* Hub cards */}
                <div className="grid sm:grid-cols-2 gap-4">
                    <HubCard
                        testId="plans-hub-planos"
                        icon="fa-box"
                        title="Planos"
                        subtitle={`${plans.length} plano${plans.length !== 1 ? "s" : ""} cadastrado${plans.length !== 1 ? "s" : ""}`}
                        onClick={() => setView("plans")}
                    />
                    <HubCard
                        testId="plans-hub-distribuicao"
                        icon="fa-chart-pie"
                        title="Distribuição"
                        subtitle="Tenants e MRR por plano"
                        onClick={() => setView("distribution")}
                    />
                </div>
            </div>
        );
    }

    // ─── PLANOS ────────────────────────────────────────────────────────
    if (view === "plans") {
        return (
            <>
                <SectionShell
                    title="Planos comerciais"
                    subtitle={`${plans.length} plano${plans.length !== 1 ? "s" : ""} cadastrado${plans.length !== 1 ? "s" : ""}`}
                    onBack={() => setView("hub")}
                    actions={
                        <button
                            data-testid="plan-new-btn"
                            onClick={() => setEditing("new")}
                            className="h-10 px-4 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-xs uppercase tracking-widest inline-flex items-center gap-2"
                        >
                            <i className="fa-solid fa-plus"></i>
                            Novo plano
                        </button>
                    }
                >
                    {loading && <div className="text-slate-500 text-sm py-8 text-center">Carregando...</div>}
                    {!loading && plans.length === 0 && (
                        <div className="text-slate-500 text-sm py-10 text-center border border-white/5 rounded-2xl">
                            Nenhum plano cadastrado. Crie o primeiro.
                        </div>
                    )}
                    <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
                        {plans.map((p) => (
                            <PlanRowCard
                                key={p.id}
                                plan={p}
                                onEdit={() => setEditing(p)}
                                onPreview={() => setPreviewing(p)}
                                onToggle={async () => {
                                    try {
                                        await plansAdminApi.toggle(p.id);
                                        toast.success(p.active ? "Plano desativado" : "Plano ativado");
                                        await load();
                                    } catch (e) { toast.error("Falha", { description: e.message }); }
                                }}
                            />
                        ))}
                    </div>
                </SectionShell>

                {editing && (
                    <PlanEditorModal
                        plan={editing === "new" ? null : editing}
                        tenantCount={editing === "new" ? 0 : (editing.tenant_count || 0)}
                        onClose={() => setEditing(null)}
                        onSaved={async () => {
                            setEditing(null);
                            await load();
                        }}
                        onPreview={(draft) => setPreviewing(draft)}
                    />
                )}

                {previewing && (
                    <PreviewOverlay plan={previewing} onClose={() => setPreviewing(null)} />
                )}
            </>
        );
    }

    // ─── DISTRIBUIÇÃO ──────────────────────────────────────────────────
    if (view === "distribution") {
        return (
            <SectionShell
                title="Distribuição por plano"
                subtitle="Tenants ativos e MRR gerado"
                onBack={() => setView("hub")}
            >
                <DistributionView data={distribution} loading={loading} />
            </SectionShell>
        );
    }

    return null;
};

// ─── Sub-components ────────────────────────────────────────────────────────

const KpiCard = ({ icon, label, value, testId }) => (
    <div
        data-testid={testId}
        className="rounded-2xl border border-white/5 bg-black/40 p-4 flex items-center gap-3"
    >
        <div className="h-10 w-10 rounded-xl bg-amber-500/10 border border-amber-500/25 text-amber-300 flex items-center justify-center">
            <i className={`fa-solid ${icon}`}></i>
        </div>
        <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
            <div className="text-lg text-white font-semibold truncate">{value}</div>
        </div>
    </div>
);

const HubCard = ({ icon, title, subtitle, onClick, testId }) => (
    <button
        data-testid={testId}
        onClick={onClick}
        className="text-left rounded-2xl border border-white/5 bg-black/40 hover:border-amber-500/40 hover:bg-black/60 p-5 transition-all group"
    >
        <div className="h-12 w-12 rounded-xl bg-amber-500/10 border border-amber-500/25 text-amber-300 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
            <i className={`fa-solid ${icon} text-lg`}></i>
        </div>
        <div className="salon-heading text-lg text-white">{title}</div>
        <div className="text-xs text-slate-400 mt-0.5">{subtitle}</div>
        <div className="mt-3 text-[10px] uppercase tracking-widest text-amber-300/70 inline-flex items-center gap-1">
            Abrir <i className="fa-solid fa-arrow-right text-[9px]"></i>
        </div>
    </button>
);

const SectionShell = ({ title, subtitle, onBack, children, actions }) => (
    <div className="space-y-5" data-testid="admin-plans-section">
        <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
                <button
                    data-testid="admin-plans-back"
                    onClick={onBack}
                    className="h-10 w-10 rounded-xl border border-white/10 hover:border-amber-500/40 text-slate-400 hover:text-amber-300 flex items-center justify-center transition-all"
                >
                    <i className="fa-solid fa-arrow-left"></i>
                </button>
                <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500">Planos</div>
                    <h2 className="salon-heading text-2xl text-white">{title}</h2>
                    {subtitle && <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>}
                </div>
            </div>
            {actions}
        </div>
        {children}
    </div>
);

const PlanRowCard = ({ plan, onEdit, onPreview, onToggle }) => {
    const featureCount = Object.values(plan.features || {}).filter(Boolean).length;
    return (
        <div
            data-testid={`plan-row-${plan.slug}`}
            className={`rounded-2xl border p-4 flex flex-col gap-3 transition-all ${
                plan.active
                    ? "border-white/10 bg-black/40 hover:border-amber-500/30"
                    : "border-white/5 bg-black/20 opacity-70"
            }`}
        >
            <div className="flex items-start gap-3">
                <div
                    className="h-11 w-11 rounded-xl flex items-center justify-center border shrink-0"
                    style={{ backgroundColor: `${plan.color}22`, borderColor: `${plan.color}55`, color: plan.color }}
                >
                    <i className="fa-solid fa-crown"></i>
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="salon-heading text-lg text-white truncate">{plan.name}</h3>
                        <span
                            data-testid={`plan-status-${plan.slug}`}
                            className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full border ${
                                plan.active
                                    ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10"
                                    : "border-slate-500/40 text-slate-400 bg-slate-500/10"
                            }`}
                        >
                            {plan.active ? "Ativo" : "Inativo"}
                        </span>
                        {plan.admin_only && (
                            <span
                                data-testid={`plan-adminonly-${plan.slug}`}
                                title="Oculto do público — só o Super Admin atribui"
                                className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full border border-violet-500/40 text-violet-300 bg-violet-500/10 inline-flex items-center gap-1"
                            >
                                <i className="fa-solid fa-eye-slash text-[8px]"></i>Oculto
                            </span>
                        )}
                    </div>
                    <div className="text-[11px] text-slate-500 font-mono truncate">{plan.slug}</div>
                </div>
            </div>

            <div className="flex items-baseline gap-1 border-t border-white/5 pt-3">
                <span className="salon-heading text-3xl text-white">{brl(plan.price_monthly)}</span>
                <span className="text-xs text-slate-500">/mês</span>
                {plan.trial_days > 0 && (
                    <span className="ml-auto text-[10px] text-emerald-300">{plan.trial_days}d grátis</span>
                )}
            </div>

            <div className="text-[11px] text-slate-400 flex items-center gap-3">
                <span><i className="fa-solid fa-users text-amber-300/70 mr-1"></i>{plan.tenant_count} tenants</span>
                <span><i className="fa-solid fa-list-check text-amber-300/70 mr-1"></i>{featureCount} features</span>
            </div>

            <div className="grid grid-cols-3 gap-1.5 pt-2 border-t border-white/5">
                <button
                    data-testid={`plan-edit-${plan.slug}`}
                    onClick={onEdit}
                    className="h-8 rounded-lg border border-white/10 hover:border-amber-500/50 text-xs text-slate-300 hover:text-amber-300"
                >
                    <i className="fa-solid fa-pen mr-1"></i>Editar
                </button>
                <button
                    data-testid={`plan-preview-${plan.slug}`}
                    onClick={onPreview}
                    className="h-8 rounded-lg border border-white/10 hover:border-sky-500/50 text-xs text-slate-300 hover:text-sky-300"
                >
                    <i className="fa-solid fa-eye mr-1"></i>Preview
                </button>
                <button
                    data-testid={`plan-toggle-${plan.slug}`}
                    onClick={onToggle}
                    className={`h-8 rounded-lg border text-xs ${
                        plan.active
                            ? "border-red-500/30 text-red-300 hover:bg-red-500/10"
                            : "border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10"
                    }`}
                >
                    <i className={`fa-solid ${plan.active ? "fa-pause" : "fa-play"} mr-1`}></i>
                    {plan.active ? "Desativar" : "Ativar"}
                </button>
            </div>
        </div>
    );
};

// ─── Editor Modal ─────────────────────────────────────────────────────────
const PlanEditorModal = ({ plan, tenantCount, onClose, onSaved, onPreview }) => {
    const isNew = !plan;
    const [form, setForm] = useState(() => ({
        name: plan?.name || "",
        slug: plan?.slug || "",
        color: plan?.color || "#c9983a",
        description: plan?.description || "",
        price_monthly: plan?.price_monthly ?? 0,
        price_annual: plan?.price_annual ?? "",
        trial_days: plan?.trial_days ?? 14,
        active: plan?.active !== false,
        admin_only: plan?.admin_only === true,
        stripe_price_id: plan?.stripe_price_id || "",
        limits: { ...DEFAULT_LIMITS, ...(plan?.limits || {}) },
        features: { ...DEFAULT_FEATURES, ...(plan?.features || {}) },
    }));
    const [showConfirm, setShowConfirm] = useState(false);
    const [saving, setSaving] = useState(false);

    const set = (patch) => setForm((f) => ({ ...f, ...patch }));
    const setLimit = (k, v) => setForm((f) => ({ ...f, limits: { ...f.limits, [k]: v } }));
    const setFeature = (k, v) => setForm((f) => ({ ...f, features: { ...f.features, [k]: v } }));

    // Auto-slug quando novo
    useEffect(() => {
        if (isNew && form.name && !form.slug) {
            const auto = form.name.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
            set({ slug: auto });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.name]);

    const buildDraftPlan = () => ({
        ...(plan || {}),
        name: form.name || "Novo plano",
        slug: form.slug || "novo-plano",
        color: form.color,
        description: form.description,
        price_monthly: Number(form.price_monthly) || 0,
        price_annual: form.price_annual === "" ? null : Number(form.price_annual),
        trial_days: Number(form.trial_days) || 0,
        active: form.active,
        admin_only: form.admin_only,
        stripe_price_id: form.stripe_price_id,
        limits: form.limits,
        features: form.features,
    });

    const handleSave = async () => {
        if (!form.name.trim()) {
            toast.error("Nome é obrigatório");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                name: form.name.trim(),
                slug: form.slug.trim() || undefined,
                color: form.color,
                description: form.description.trim(),
                price_monthly: Number(form.price_monthly) || 0,
                price_annual: form.price_annual === "" ? null : Number(form.price_annual),
                trial_days: Number(form.trial_days) || 0,
                active: form.active,
                admin_only: form.admin_only,
                stripe_price_id: form.stripe_price_id.trim(),
                limits: {
                    clients: Number(form.limits.clients),
                    professionals: Number(form.limits.professionals),
                    storage_gb: Number(form.limits.storage_gb),
                },
                features: form.features,
            };
            if (isNew) {
                await plansAdminApi.create(payload);
                toast.success("Plano criado");
            } else {
                await plansAdminApi.update(plan.id, payload);
                toast.success("Plano atualizado");
            }
            await onSaved();
        } catch (e) {
            toast.error("Falha ao salvar", { description: e.message });
        } finally {
            setSaving(false);
            setShowConfirm(false);
        }
    };

    return (
        <div
            data-testid="plan-editor-modal"
            className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={onClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl border border-amber-400/30 bg-slate-950 shadow-[0_30px_80px_-10px_rgba(212,175,55,0.35)]"
            >
                <header className="sticky top-0 z-10 bg-slate-950/95 backdrop-blur border-b border-white/5 px-6 py-4 flex items-center justify-between">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300/70">Gestão de Planos</div>
                        <h3 className="salon-heading text-xl text-white">{isNew ? "Novo plano" : `Editar ${plan.name}`}</h3>
                    </div>
                    <button
                        data-testid="plan-editor-close"
                        onClick={onClose}
                        className="h-9 w-9 rounded-lg border border-white/10 text-slate-400 hover:text-white hover:border-white/20"
                    >
                        <i className="fa-solid fa-xmark"></i>
                    </button>
                </header>

                <div className="p-6 space-y-6">
                    {/* Identificação */}
                    <section className="space-y-3">
                        <SectionTitle>Identificação</SectionTitle>
                        <div className="grid sm:grid-cols-2 gap-3">
                            <Input testId="plan-input-name" label="Nome" value={form.name} onChange={(v) => set({ name: v })} required />
                            <Input testId="plan-input-slug" label="Slug/código" value={form.slug} onChange={(v) => set({ slug: v })} mono />
                        </div>
                        <Input testId="plan-input-description" label="Descrição curta" value={form.description} onChange={(v) => set({ description: v })} />
                        <div className="grid sm:grid-cols-2 gap-3 items-end">
                            <div>
                                <label className="block text-[10px] uppercase tracking-widest text-slate-500 mb-1">Cor do badge</label>
                                <div className="flex items-center gap-2">
                                    <input
                                        type="color"
                                        data-testid="plan-input-color"
                                        value={form.color}
                                        onChange={(e) => set({ color: e.target.value })}
                                        className="h-10 w-16 rounded-lg border border-white/10 bg-slate-950 cursor-pointer"
                                    />
                                    <span className="text-xs text-slate-400 font-mono">{form.color}</span>
                                </div>
                            </div>
                            <Toggle
                                testId="plan-input-active"
                                label="Status"
                                value={form.active}
                                onChange={(v) => set({ active: v })}
                                onLabel="Ativo"
                                offLabel="Inativo"
                            />
                        </div>
                        <Toggle
                            testId="plan-input-admin-only"
                            label="Visibilidade"
                            value={form.admin_only}
                            onChange={(v) => set({ admin_only: v })}
                            onLabel="Somente admin (oculto do público)"
                            offLabel="Público (aparece na página de preços)"
                        />
                        {form.admin_only && (
                            <p className="text-[11px] text-violet-300/80">
                                <i className="fa-solid fa-eye-slash mr-1.5"></i>
                                Não aparece na página de preços nem no cadastro. Só o Super Admin atribui, pelo detalhe do tenant.
                            </p>
                        )}
                    </section>

                    {/* Preço */}
                    <section className="space-y-3">
                        <SectionTitle>Preço</SectionTitle>
                        <div className="grid sm:grid-cols-3 gap-3">
                            <Input testId="plan-input-price-monthly" label="Preço mensal (R$)" type="number" value={form.price_monthly} onChange={(v) => set({ price_monthly: v })} />
                            <Input testId="plan-input-price-annual" label="Preço anual (R$) — opcional" type="number" value={form.price_annual} onChange={(v) => set({ price_annual: v })} />
                            <Input testId="plan-input-trial-days" label="Trial (dias)" type="number" value={form.trial_days} onChange={(v) => set({ trial_days: v })} />
                        </div>
                        {!form.admin_only && Number(form.price_monthly) > 0 && (
                            <>
                                <Input
                                    testId="plan-input-stripe-price"
                                    label="Stripe Price ID (assinatura mensal)"
                                    value={form.stripe_price_id}
                                    onChange={(v) => set({ stripe_price_id: v })}
                                    mono
                                />
                                <p className="text-[11px] text-slate-500">
                                    Crie um Price <strong>recorrente mensal</strong> em BRL no Stripe (Product catalog) e cole o
                                    <span className="font-mono"> price_… </span> aqui. Sem isso, a assinatura por cartão não abre
                                    (o cliente ainda pode pagar por Pix). Free e Infinity não usam este campo.
                                </p>
                            </>
                        )}
                    </section>

                    {/* Limites */}
                    <section className="space-y-3">
                        <SectionTitle>Limites</SectionTitle>
                        <div className="grid sm:grid-cols-3 gap-3">
                            <LimitField testId="plan-limit-clients" label="Clientes" value={form.limits.clients} onChange={(v) => setLimit("clients", v)} />
                            <LimitField testId="plan-limit-professionals" label="Profissionais" value={form.limits.professionals} onChange={(v) => setLimit("professionals", v)} />
                            <LimitField testId="plan-limit-storage" label="Armazenamento (GB)" value={form.limits.storage_gb} onChange={(v) => setLimit("storage_gb", v)} />
                        </div>
                    </section>

                    {/* Features */}
                    <section className="space-y-3">
                        <SectionTitle>Features incluídas</SectionTitle>
                        <div className="grid sm:grid-cols-2 gap-1.5">
                            {Object.entries(FEATURE_LABELS).map(([k, meta]) => (
                                <label
                                    key={k}
                                    data-testid={`plan-feature-${k}`}
                                    className={`flex items-center gap-2.5 rounded-lg border px-3 py-2 cursor-pointer transition-all ${
                                        form.features[k]
                                            ? "border-amber-500/50 bg-amber-500/10 text-amber-100"
                                            : "border-white/5 bg-black/30 text-slate-300 hover:border-white/15"
                                    }`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={!!form.features[k]}
                                        onChange={(e) => setFeature(k, e.target.checked)}
                                        className="accent-amber-500 h-4 w-4"
                                    />
                                    <i className={`fa-solid ${meta.icon} text-amber-300/80 w-4 text-center`}></i>
                                    <span className="text-xs">{meta.label}</span>
                                </label>
                            ))}
                        </div>
                    </section>
                </div>

                <footer className="sticky bottom-0 bg-slate-950/95 backdrop-blur border-t border-white/5 px-6 py-4 flex items-center justify-between gap-3 flex-wrap">
                    <button
                        type="button"
                        data-testid="plan-preview-btn"
                        onClick={() => onPreview(buildDraftPlan())}
                        className="h-10 px-4 rounded-xl border border-sky-500/40 text-sky-200 hover:bg-sky-500/10 text-xs uppercase tracking-widest inline-flex items-center gap-2"
                    >
                        <i className="fa-solid fa-eye"></i>
                        Preview
                    </button>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="h-10 px-4 rounded-xl border border-white/10 text-slate-300 text-xs uppercase tracking-widest hover:bg-white/5"
                        >
                            Cancelar
                        </button>
                        <button
                            type="button"
                            data-testid="plan-save-btn"
                            onClick={() => {
                                if (!isNew && tenantCount > 0) {
                                    setShowConfirm(true);
                                } else {
                                    handleSave();
                                }
                            }}
                            disabled={saving}
                            className="h-10 px-5 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-xs uppercase tracking-widest disabled:opacity-50 inline-flex items-center gap-2"
                        >
                            {saving ? <><i className="fa-solid fa-spinner fa-spin"></i>Salvando...</> : <><i className="fa-solid fa-check"></i>Salvar plano</>}
                        </button>
                    </div>
                </footer>
            </div>

            {showConfirm && (
                <div
                    data-testid="plan-save-confirm"
                    className="fixed inset-0 z-[90] bg-black/85 flex items-center justify-center p-4"
                    onClick={() => !saving && setShowConfirm(false)}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="w-full max-w-md rounded-2xl border border-amber-400/40 bg-slate-950 p-6 shadow-2xl"
                    >
                        <div className="flex items-start gap-3 mb-4">
                            <div className="h-11 w-11 rounded-xl bg-amber-500/15 border border-amber-400/40 text-amber-200 flex items-center justify-center shrink-0">
                                <i className="fa-solid fa-triangle-exclamation"></i>
                            </div>
                            <div>
                                <h4 className="salon-heading text-lg text-white">Confirmar alteração</h4>
                                <p className="text-xs text-slate-400 mt-1">
                                    Esta alteração <span className="text-amber-300 font-semibold">não afetará os {tenantCount} tenant{tenantCount !== 1 ? "s" : ""}</span> já assinantes deste plano. Apenas novos cadastros usarão as novas configurações.
                                </p>
                            </div>
                        </div>
                        <div className="flex gap-2 justify-end">
                            <button
                                onClick={() => setShowConfirm(false)}
                                disabled={saving}
                                className="h-10 px-4 rounded-xl border border-white/10 text-slate-300 text-xs uppercase tracking-widest hover:bg-white/5 disabled:opacity-50"
                            >
                                Cancelar
                            </button>
                            <button
                                data-testid="plan-save-confirm-btn"
                                onClick={handleSave}
                                disabled={saving}
                                className="h-10 px-4 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-xs uppercase tracking-widest disabled:opacity-50"
                            >
                                {saving ? "Salvando..." : "Entendi — Salvar"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// ─── Preview Overlay ──────────────────────────────────────────────────────
const PreviewOverlay = ({ plan, onClose }) => (
    <div
        data-testid="plan-preview-overlay"
        className="fixed inset-0 z-[85] bg-black/85 backdrop-blur-sm flex items-center justify-center p-6"
        onClick={onClose}
    >
        <div onClick={(e) => e.stopPropagation()} className="w-full max-w-sm relative">
            <button
                data-testid="plan-preview-close"
                onClick={onClose}
                className="absolute -top-10 right-0 text-slate-300 hover:text-white text-xs uppercase tracking-widest"
            >
                Fechar <i className="fa-solid fa-xmark ml-1"></i>
            </button>
            <div className="text-center text-[10px] uppercase tracking-[0.3em] text-amber-300/70 mb-3">
                Preview no site de vendas
            </div>
            <PlanCard plan={plan} highlighted variant="preview" testId="plan-preview-card" />
        </div>
    </div>
);

// ─── Distribution View ────────────────────────────────────────────────────
const DistributionView = ({ data, loading }) => {
    if (loading) return <div className="text-slate-500 text-sm py-8 text-center">Carregando...</div>;
    if (!data || !data.rows || data.rows.length === 0) {
        return <div className="text-slate-500 text-sm py-10 text-center border border-white/5 rounded-2xl">Sem dados de distribuição ainda.</div>;
    }
    const chartData = data.rows.filter((r) => r.tenants_active > 0).map((r) => ({ name: r.name, value: r.tenants_active, color: r.color }));

    return (
        <div className="space-y-5">
            <div className="grid md:grid-cols-2 gap-4">
                <div className="rounded-2xl border border-white/5 bg-black/40 p-4">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Distribuição de tenants ativos</div>
                    {chartData.length === 0 ? (
                        <div className="h-64 flex items-center justify-center text-slate-500 text-xs">Sem tenants ativos ainda</div>
                    ) : (
                        <ResponsiveContainer width="100%" height={280}>
                            <PieChart>
                                <Pie
                                    data={chartData}
                                    dataKey="value"
                                    nameKey="name"
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={90}
                                    label={({ name, value }) => `${name}: ${value}`}
                                >
                                    {chartData.map((entry) => (
                                        <Cell key={entry.name} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }} />
                                <Legend wrapperStyle={{ fontSize: 11 }} />
                            </PieChart>
                        </ResponsiveContainer>
                    )}
                </div>

                <div className="rounded-2xl border border-white/5 bg-black/40 p-4 space-y-2">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Resumo</div>
                    <SummaryLine label="MRR total" value={brl(data.total_mrr)} accent />
                    <SummaryLine label="Tenants ativos" value={data.total_tenants_active} />
                    <SummaryLine label="Planos ativos" value={data.total_plans_active} />
                    {data.most_popular && (
                        <SummaryLine label="Plano mais popular" value={`${data.most_popular.name} (${data.most_popular.tenants_active})`} />
                    )}
                </div>
            </div>

            <div className="rounded-2xl border border-white/5 bg-black/40 overflow-hidden" data-testid="plan-distribution-table">
                <table className="w-full text-sm">
                    <thead className="bg-black/60 text-[10px] uppercase tracking-widest text-slate-500">
                        <tr>
                            <th className="text-left px-4 py-3">Plano</th>
                            <th className="text-right px-4 py-3">Preço</th>
                            <th className="text-right px-4 py-3">Tenants ativos</th>
                            <th className="text-right px-4 py-3">MRR</th>
                            <th className="text-right px-4 py-3">% do MRR</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                        {data.rows.map((r) => (
                            <tr key={r.plan_id} data-testid={`plan-dist-row-${r.slug}`} className="hover:bg-white/[0.02]">
                                <td className="px-4 py-3 flex items-center gap-2">
                                    <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: r.color }} />
                                    <span className="text-white">{r.name}</span>
                                </td>
                                <td className="px-4 py-3 text-right text-slate-300">{brl(r.price_monthly)}</td>
                                <td className="px-4 py-3 text-right text-slate-300">{r.tenants_active}</td>
                                <td className="px-4 py-3 text-right text-amber-300 font-semibold">{brl(r.mrr)}</td>
                                <td className="px-4 py-3 text-right text-slate-400">{r.pct_mrr}%</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const SummaryLine = ({ label, value, accent }) => (
    <div className="flex items-center justify-between py-1.5 border-b border-white/5 last:border-0">
        <span className="text-xs text-slate-400">{label}</span>
        <span className={`text-sm font-semibold ${accent ? "text-amber-300" : "text-white"}`}>{value}</span>
    </div>
);

// ─── Form primitives ──────────────────────────────────────────────────────
const SectionTitle = ({ children }) => (
    <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300/70 font-bold border-b border-white/5 pb-2">
        {children}
    </div>
);

const Input = ({ testId, label, value, onChange, type = "text", mono = false, required = false }) => (
    <div>
        <label className="block text-[10px] uppercase tracking-widest text-slate-500 mb-1">
            {label} {required && <span className="text-red-400">*</span>}
        </label>
        <input
            data-testid={testId}
            type={type}
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            className={`w-full h-10 px-3 rounded-lg border border-white/10 bg-slate-950/70 text-sm text-white focus:border-amber-500/50 focus:outline-none ${mono ? "font-mono" : ""}`}
        />
    </div>
);

const Toggle = ({ testId, label, value, onChange, onLabel = "Ativo", offLabel = "Inativo" }) => (
    <div>
        <label className="block text-[10px] uppercase tracking-widest text-slate-500 mb-1">{label}</label>
        <button
            type="button"
            data-testid={testId}
            onClick={() => onChange(!value)}
            className={`h-10 w-full rounded-lg border text-xs font-semibold flex items-center justify-center gap-2 transition-all ${
                value
                    ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200"
                    : "border-white/10 bg-black/30 text-slate-400"
            }`}
        >
            <i className={`fa-solid ${value ? "fa-toggle-on" : "fa-toggle-off"}`}></i>
            {value ? onLabel : offLabel}
        </button>
    </div>
);

const LimitField = ({ testId, label, value, onChange }) => {
    const unlimited = value === -1;
    return (
        <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-500 mb-1">{label}</label>
            <div className="flex gap-1">
                <input
                    data-testid={`${testId}-input`}
                    type="number"
                    disabled={unlimited}
                    value={unlimited ? "" : value}
                    onChange={(e) => onChange(e.target.value === "" ? 0 : Number(e.target.value))}
                    className="flex-1 min-w-0 h-10 px-3 rounded-lg border border-white/10 bg-slate-950/70 text-sm text-white disabled:opacity-40 focus:border-amber-500/50 focus:outline-none"
                    placeholder={unlimited ? "Ilimitado" : "0"}
                />
                <button
                    type="button"
                    data-testid={`${testId}-unlimited`}
                    onClick={() => onChange(unlimited ? 0 : -1)}
                    className={`px-2 rounded-lg border text-[10px] uppercase tracking-wider ${
                        unlimited
                            ? "border-amber-500/50 bg-amber-500/15 text-amber-200"
                            : "border-white/10 text-slate-400 hover:border-white/20"
                    }`}
                    title="Alternar ilimitado"
                >
                    ∞
                </button>
            </div>
        </div>
    );
};

export default AdminPlans;
